import { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { useFlags } from '@atlaskit/flag';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import type { BulkFavoriteItem } from '@atlassian/rovo-conversation-actions/prompt-library-toolbar';

import { getIsSystemPromptForAnalytics } from '../../common/utils/getIsSystemPromptForAnalytics';
import promptLibraryMessages from '../../ui/prompt-library/messages';

import { useBulkUpdatePrivacyController } from './useBulkUpdatePrivacyController';
import { useFavoritePromptsController } from './useFavoritePromptsController';

export function usePromptActions({
	assistanceServiceParams,
	showErrorFlag = true,
	onSuccess,
	deletePrompt,
	refetchPrompts,
	updatePromptFavoriteInCache,
	bulkUpdatePromptFavoritesInCache,
}: {
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
	showErrorFlag?: boolean;
	/** Called after a successful favorite toggle to allow the caller to refetch data */
	onSuccess?: () => void;
	/** Required for handleBulkDelete - the function to delete a single prompt */
	deletePrompt?: (promptId: string) => Promise<void>;
	/** Required for handleBulkDelete - the function to refetch prompts after deletion */
	refetchPrompts?: () => Promise<unknown>;
	/** Optional callback to optimistically update the favorite status in cache */
	updatePromptFavoriteInCache?: (promptId: string, isFavorited: boolean) => void;
	/** Optional callback to optimistically update multiple prompts' favorite status in cache */
	bulkUpdatePromptFavoritesInCache?: (
		updates: Array<{ promptId: string; isFavorited: boolean }>,
	) => void;
}): {
	handleFavoriteToggle: (promptId: string, isFavorited: boolean) => Promise<void>;
	handleBulkFavorite: (prompts: BulkFavoriteItem[]) => Promise<void>;
	handleBulkDelete: (promptIds: string[]) => Promise<void>;
	handleBulkShare: (promptIds: string[]) => Promise<void>;
} {
	const { favoritePrompts } = useFavoritePromptsController({
		assistanceServiceParams,
	});

	const { bulkUpdatePrivacy } = useBulkUpdatePrivacyController({
		assistanceServiceParams,
	});

	let showFlag: ReturnType<typeof useFlags>['showFlag'] | null = null;
	try {
		const flagContext = useFlags();
		showFlag = flagContext?.showFlag || null;
	} catch {
		// FlagProvider context not available (e.g., in tests)
	}

	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const handleFavoriteToggle = useCallback(
		async (promptId: string, isFavorited: boolean, isUserGenerated?: boolean) => {
			updatePromptFavoriteInCache?.(promptId, isFavorited);

			try {
				await favoritePrompts({
					prompts: [
						{
							shareId: promptId,
							isFavorite: isFavorited,
						},
					],
				});
				// Fire analytics only after successful API call to avoid over-reporting
				sendAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'promptLibraryFavoriteToggle',
					attributes: {
						favoriteAction: isFavorited ? 'favorited' : 'unfavorited',
						isSystemPrompt: getIsSystemPromptForAnalytics(isUserGenerated),
					},
				});
				onSuccess?.();
			} catch (error) {
				// Revert optimistic update on failure
				updatePromptFavoriteInCache?.(promptId, !isFavorited);

				if (showErrorFlag && showFlag) {
					showFlag({
						id: 'prompt-library.favorite-toggle-error',
						title: formatMessage(
							isFavorited
								? promptLibraryMessages.failedToAddPromptToFavorites
								: promptLibraryMessages.failedToRemovePromptFromFavorites,
						),
						description: formatMessage(promptLibraryMessages.tryAgain),
						appearance: 'error',
						isAutoDismiss: true,
					});
				}
				throw error;
			}
		},
		[
			favoritePrompts,
			showErrorFlag,
			showFlag,
			formatMessage,
			sendAnalyticsEvent,
			onSuccess,
			updatePromptFavoriteInCache,
		],
	);

	/**
	 * Bulk favorite/unfavorite multiple prompts in a single API call.
	 * Used by the multiselect toolbar for batch operations.
	 * @param prompts - Array of prompts to favorite/unfavorite
	 * @param systemPromptCount - Number of system prompts in the selection (for analytics)
	 */
	const handleBulkFavorite = useCallback(
		async (prompts: BulkFavoriteItem[], systemPromptCount: number = 0) => {
			// Optimistically update all prompts in a single cache update
			bulkUpdatePromptFavoritesInCache?.(
				prompts.map((p) => ({ promptId: p.shareId, isFavorited: p.isFavorite })),
			);

			try {
				await favoritePrompts({ prompts });
				// Fire analytics for bulk operation
				sendAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'promptLibraryBulkFavoriteToggle',
					attributes: {
						count: prompts.length,
						favoriteAction: prompts[0]?.isFavorite ? 'favorited' : 'unfavorited',
						systemPromptCount,
					},
				});
				onSuccess?.();
			} catch (error) {
				// Revert optimistic updates on failure
				bulkUpdatePromptFavoritesInCache?.(
					prompts.map((p) => ({ promptId: p.shareId, isFavorited: !p.isFavorite })),
				);

				if (showErrorFlag && showFlag) {
					const isFavoriting = prompts[0]?.isFavorite ?? true;
					showFlag({
						id: 'prompt-library.bulk-favorite-toggle-error',
						title: formatMessage(
							isFavoriting
								? promptLibraryMessages.failedToAddPromptToFavorites
								: promptLibraryMessages.failedToRemovePromptFromFavorites,
						),
						description: formatMessage(promptLibraryMessages.tryAgain),
						appearance: 'error',
						isAutoDismiss: true,
					});
				}
				throw error;
			}
		},
		[
			favoritePrompts,
			showErrorFlag,
			showFlag,
			formatMessage,
			sendAnalyticsEvent,
			onSuccess,
			bulkUpdatePromptFavoritesInCache,
		],
	);

	/**
	 * Bulk delete multiple prompts and refetch the list.
	 * Used by the multiselect toolbar for batch delete operations.
	 * Requires deletePrompt and refetchPrompts to be provided to the hook.
	 * @param promptIds - Array of prompt IDs to delete
	 * @param systemPromptCount - Number of system prompts in the selection (for analytics)
	 */
	const handleBulkDelete = useCallback(
		async (promptIds: string[], systemPromptCount: number = 0) => {
			if (!deletePrompt || !refetchPrompts) {
				throw new Error('handleBulkDelete requires deletePrompt and refetchPrompts');
			}
			try {
				await Promise.all(promptIds.map((id) => deletePrompt(id)));
				// Fire analytics for bulk delete
				sendAnalyticsEvent({
					action: 'deleted',
					actionSubject: 'prompt',
					actionSubjectId: 'promptBulkDeleted',
					attributes: {
						isBulkDelete: true,
						bulkDeleteCount: promptIds.length,
						systemPromptCount,
					},
				});
				await refetchPrompts();
			} catch (error) {
				if (showErrorFlag && showFlag) {
					showFlag({
						id: 'prompt-library.bulk-delete-error',
						title: formatMessage(promptLibraryMessages.failedToDeletePrompt),
						description: formatMessage(promptLibraryMessages.tryAgain),
						appearance: 'error',
						isAutoDismiss: true,
					});
				}
				throw error;
			}
		},
		[deletePrompt, refetchPrompts, showErrorFlag, showFlag, formatMessage, sendAnalyticsEvent],
	);

	/**
	 * Bulk share multiple prompts by making them public.
	 * Used by the multiselect toolbar for batch share operations.
	 * @param promptIds - Array of prompt IDs to share
	 * @param systemPromptCount - Number of system prompts in the selection (for analytics)
	 */
	const handleBulkShare = useCallback(
		async (promptIds: string[], systemPromptCount: number = 0) => {
			try {
				await bulkUpdatePrivacy({
					prompts: promptIds.map((shareId) => ({
						shareId,
						isPrivate: false,
					})),
				});
				sendAnalyticsEvent({
					action: 'shared',
					actionSubject: 'prompt',
					actionSubjectId: 'promptBulkShared',
					attributes: {
						bulkShareCount: promptIds.length + systemPromptCount,
						systemPromptCount,
					},
				});
			} catch (error) {
				if (showErrorFlag && showFlag) {
					showFlag({
						id: 'prompt-library.bulk-share-error',
						title: formatMessage(promptLibraryMessages.failedToSharePrompt),
						description: formatMessage(promptLibraryMessages.tryAgain),
						appearance: 'error',
						isAutoDismiss: true,
					});
				}
				throw error;
			}
		},
		[bulkUpdatePrivacy, showErrorFlag, showFlag, formatMessage, sendAnalyticsEvent],
	);

	return { handleFavoriteToggle, handleBulkFavorite, handleBulkDelete, handleBulkShare };
}
